import {RasterDataType} from "@luciad/ria/model/tileset/RasterDataType";
import {RasterTileSetModel} from "@luciad/ria/model/tileset/RasterTileSetModel";
import {ContextMenu} from "@luciad/ria/view/ContextMenu";
import {FeatureLayer} from "@luciad/ria/view/feature/FeatureLayer";
import {GridLayer} from "@luciad/ria/view/grid/GridLayer";
import {RasterImageLayer} from "@luciad/ria/view/image/RasterImageLayer";
import {LayerGroup} from "@luciad/ria/view/LayerGroup";
import {LayerTreeNode} from "@luciad/ria/view/LayerTreeNode";
import {LayerTreeNodeType} from "@luciad/ria/view/LayerTreeNodeType";
import {Map} from "@luciad/ria/view/Map";
import {PaintRepresentation} from "@luciad/ria/view/PaintRepresentation";
import {RasterTileSetLayer} from "@luciad/ria/view/tileset/RasterTileSetLayer";
import React, {MouseEvent, ReactNode, useState} from "react";
import {useContextMenuContext} from "../../hooks/useContextMenu";
import {useLayerLabel} from "../../hooks/useLayerLabel";
import {useLayerVisible} from "../../hooks/useLayerVisible";
import {useToastContext} from "../../hooks/useToast";
import {getFitBounds} from "../../util/FitBoundsUtil";
import {CollapsablePanel} from "../CollapsablePanel";
import {Draggable, DraggedOverPart} from "../Draggable";
import {Modal} from "../Modal";
import "./LayerTreeControl.css";
import {useRerenderOnLayerTreeEvent} from "../../hooks/useRerenderOnLayerTreeEvent";
import {ProgressRing} from "../ProgressRing";
import {ProgressManager} from "../../util/ProgressManager";
import {useProgress} from "../../hooks/useProgress";

export interface LayerSettings {
  canHandleLayer(layer: LayerTreeNode): boolean;

  render(map: Map, layer: LayerTreeNode): ReactNode;
}

interface Props {
  map: Map;
  layerSettings?: LayerSettings[];
  isCollapsed?: boolean;
  progressManager?: ProgressManager<LayerTreeNode>;
}

export const LayerTreeControl = ({map, layerSettings = [], isCollapsed, progressManager}: Props) => {
  const children = map.layerTree.children;
  const [settingsLayer, setSettingsLayer] = useState<LayerTreeNode | null>(null);
  const [settings, setSettings] = useState<LayerSettings[] | null>(null);

  // Duplicating all LayerTree state in React is overkill. It's only necessary to
  // re-render the component when RIA fires an event
  useRerenderOnLayerTreeEvent(map.layerTree, ["NodeAdded", "NodeMoved", "NodeRemoved"]);

  const handleOpenSettings = (layer: LayerTreeNode) => {
    const settings = layerSettings.filter(setting => setting.canHandleLayer(layer));
    setSettingsLayer(layer);
    setSettings(settings);
  }

  const handleHideSettingsModal = () => {
    setSettingsLayer(null);
    setSettings(null);
  }

  const settingsModalOpen = settingsLayer !== null;
  const modalTitle = `Layer settings for "${settingsLayer ? settingsLayer.label : "UNKNOWN"}"`;

  return (
      <CollapsablePanel className="layer-tree-control" header="LAYERS" headerIcon="lcd-icon-layer"
                        isCollapsed={isCollapsed ? isCollapsed : undefined}>
        <div className="layer-tree-body">
          {children.reverse().map(child => {
            const showLayerSettingsButton = layerSettings.find(setting => setting.canHandleLayer(child));
            return <LayerTreeControlNode key={child.id} node={child} map={map}
                                         onOpenSettings={showLayerSettingsButton ? handleOpenSettings : null}
                                         progressManager={progressManager}/>
          })}
        </div>
        <Modal show={settingsModalOpen} title={modalTitle} onHide={handleHideSettingsModal} backdrop={false}
               position="top">
          {settingsLayer && settings && <LayerSettingsPanel map={map} layer={settingsLayer} settings={settings}/>}
        </Modal>
      </CollapsablePanel>
  )
};

interface NodeProps {
  node: LayerTreeNode;
  map: Map;
  layerSettings?: LayerSettings[];
  onOpenSettings: ((node: LayerTreeNode) => void) | null;
  progressManager?: ProgressManager;
}

const getDragOverClassName = (draggedOverPart: DraggedOverPart) => {
  switch (draggedOverPart) {
  case "NONE":
    return "";
  case "TOP":
    return "drag-over-top";
  case "BOTTOM":
    return "drag-over-bottom";
  }
}

const determineLayerInsertPosition = (node: LayerTreeNode,
                                      draggedOverPart: DraggedOverPart): "above" | "top" | "below" => {
  if (draggedOverPart === "TOP") {
    return "above";
  }
  const isLayerGroup = node.treeNodeType === LayerTreeNodeType.LAYER_GROUP;
  return isLayerGroup ? "top" : "below";
}

const getIconClassAndTitle = (node: LayerTreeNode, open: boolean): { iconClass: string, iconTitle: string } => {
  if (node instanceof FeatureLayer) {
    return {iconClass: "lcd-icon-shapes", iconTitle: "Vector layer"};
  }
  if (node instanceof RasterTileSetLayer && node.model instanceof RasterTileSetModel) {
    const model = node.model as RasterTileSetModel;
    const isElevation = (model.dataType && model.dataType === RasterDataType.ELEVATION);
    return {
      iconClass: isElevation ? "lcd-icon-height" : "lcd-icon-raster",
      iconTitle: isElevation ? "Elevation layer" : "Raster imagery layer"
    };
  }
  if (node instanceof RasterImageLayer) {
    return {
      iconClass: "lcd-icon-raster",
      iconTitle: "Raster imagery layer"
    };
  }
  if (node instanceof GridLayer) {
    return {
      iconClass: "lcd-icon-grid",
      iconTitle: "Grid layer"
    };
  }
  if (node.treeNodeType === LayerTreeNodeType.LAYER_GROUP) {
    return {
      iconClass: open ? "lcd-icon-folder" : "lcd-icon-folder-closed",
      iconTitle: "Layer group"
    };
  }
  return {
    iconClass: "lcd-icon-unknown",
    iconTitle: "Unknown layer type"
  };
}

const LayerTreeControlNode = ({
                                node,
                                map,
                                layerSettings = [],
                                onOpenSettings,
                                progressManager,
                              }: NodeProps) => {
  const visible = useLayerVisible(node);
  const label = useLayerLabel(node);

  const [open, setOpen] = useState(true);
  const [draggedOverPart, setDraggedOverPart] = useState<DraggedOverPart>("NONE");
  const {showContextMenu} = useContextMenuContext();
  const {showErrorToast} = useToastContext();
  const progress = useProgress(node, progressManager);

  const toggleVisibility = () => {
    node.visible = !node.visible;
  }

  const fitLayer = async () => {
    const bounds = await getFitBounds(node);
    if (bounds) {
      await map.mapNavigator.fit({bounds, animate: true}).catch(() => {
      });
    }
  }

  const deleteLayer = () => {
    node.parent?.removeChild(node);
  }

  const toggleOpen = () => {
    if (node.treeNodeType === LayerTreeNodeType.LAYER_GROUP) {
      setOpen((o) => !o);
    }
  };

  const handleDrop = (layerToMoveId: string) => {
    const insertPosition = determineLayerInsertPosition(node, draggedOverPart);
    const layerToMove = map.layerTree.findLayerById(layerToMoveId);
    try {
      if (insertPosition === "top" && node.treeNodeType === LayerTreeNodeType.LAYER_GROUP) {
        (node as LayerGroup).moveChild(layerToMove, "top");
      } else {
        node.parent?.moveChild(layerToMove, insertPosition, node);
      }
    } catch (e) {
      showErrorToast("Cannot move layer to this position", e);
    }
  };

  const visibleClass = visible ? "visible" : "";
  const dragOverClass = getDragOverClassName(draggedOverPart);
  const {iconClass, iconTitle} = getIconClassAndTitle(node, open);

  const renderChildren = () => {
    const children = node.children;
    if (!open || children.length === 0) {
      return null;
    }
    return (
        <div className="layer-tree-node-children">
          {children.reverse().map(child => {
            const showLayerSettingsButton = layerSettings.find(setting => setting.canHandleLayer(child));
            return <LayerTreeControlNode key={child.id} node={child} map={map}
                                         onOpenSettings={showLayerSettingsButton ? onOpenSettings : null}
                                         progressManager={progressManager}/>;
          })}
        </div>
    );
  }

  const handleContextMenuClick = (ev: MouseEvent<HTMLSpanElement>) => {
    const contextMenu = new ContextMenu();

    if (node instanceof FeatureLayer || node instanceof GridLayer) {
      if (node.isPaintRepresentationSupported(PaintRepresentation.LABEL)) {
        const labelingEnabled = node.isPaintRepresentationVisible(PaintRepresentation.LABEL);
        contextMenu.addItem({
          label: labelingEnabled ? "Disable labeling" : "Enable labeling",
          action: () => {
            node.setPaintRepresentationVisible(PaintRepresentation.LABEL, !labelingEnabled);
          },
          iconClass: "lcd-icon-label"
        });
      }
    }

    if (node instanceof FeatureLayer) {
      contextMenu.addItem({
        label: node.selectable ? "Disable selection" : "Enable selection",
        action: () => {
          node.selectable = !node.selectable;
        },
        iconClass: "lcd-icon-selectable"
      });

      contextMenu.addItem({
        label: node.hoverable ? "Disable hovering" : "Enable hovering",
        action: () => {
          node.hoverable = !node.hoverable;
        },
        iconClass: "lcd-icon-hand"
      });
    }

    if (onOpenSettings) {
      contextMenu.addItem({
        label: "Layer settings",
        action: () => onOpenSettings(node),
        iconClass: "lcd-icon-layer-properties"
      });
    }

    contextMenu.addItem({
      label: "Delete layer",
      action: deleteLayer,
      iconClass: "lcd-icon-delete"
    });

    showContextMenu([ev.pageX, ev.pageY], contextMenu);
  }

  return (
      <Draggable
          className="layer-tree-node"
          id={node.id}
          onDragOverPartChange={setDraggedOverPart}
          onDrop={handleDrop}
      >
        <div className={`layer-tree-node-row ${dragOverClass}`}>
          <span className={`layer-tree-node-icon lcd-icon ${iconClass}`} title={iconTitle} onClick={toggleOpen}/>
          <span className="layer-tree-node-label" title={label} onDoubleClick={fitLayer}>{label}</span>
          {
            progress &&
            <div className="layer-tree-progress-info">
              <span className={`layer-tree-node-progress-indicator not-show-on-hover`}>
                <ProgressRing progress={progress} radius={16} strokeWidth={3}/>
              </span>
            </div>
          }
          <div className="layer-tree-node-buttons">
            <span className="layer-tree-node-button only-show-on-hover">
              <span className="lcd-icon lcd-icon-dots" onClick={handleContextMenuClick} title="More actions"/>
            </span>
            <span className="layer-tree-node-button only-show-on-hover">
              <span className="lcd-icon lcd-icon-fit" onClick={fitLayer} title="Fit to data"/>
            </span>
            <span className={`layer-tree-node-button layer-tree-node-visibility-button ${visibleClass}`}>
              <span className="lcd-icon lcd-icon-visible" onClick={toggleVisibility} title="Toggle visibility"/>
            </span>
          </div>
        </div>
        {renderChildren()}
      </Draggable>
  );
}

interface LayerSettingsPanelProps {
  map: Map;
  layer: LayerTreeNode;
  settings: LayerSettings[];
}

const LayerSettingsPanel = ({map, layer, settings}: LayerSettingsPanelProps) => {
  if (settings.length === 0) {
    return (
        <h1>No layer settings for layer "{layer.label ?? "UNKNOWN"}"</h1>
    )
  }
  return (
      <div className="layer-settings">
        {settings.map(setting => {
          return setting.render(map, layer);
        })}
      </div>
  )
}